import { useState, useMemo, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { SortableLedgerRow } from './SortableLedgerRow';
import { HorizontalScroller } from '@/components/ui/HorizontalScroller';
import type { Ledger, LedgerType } from '@/api/types';

type SortField = 'label' | 'mwrr' | 'profitLoss' | 'gainPercentage' | 'value' | 'weight';
type SortDirection = 'asc' | 'desc';

interface LedgerListProps {
    ledgers: Ledger[];
    type: LedgerType;
    onConfirmUpdate: (ledgerId: number) => void;
    onRevertUpdate: (ledgerId: number) => void;
    onReorder?: (ledgerId: number, newWeight: number) => void;
    isUpdating: boolean;
    isDragEnabled?: boolean;
    userLatestConfirmationDate?: number | null;
    /** ID of the ledger currently being updated (for per-ledger loading state) */
    updatingLedgerId?: number | null;
}

/**
 * Sortable list of ledgers with responsive mobile/desktop layouts.
 * Supports drag-and-drop reordering via dnd-kit.
 * Mobile: Horizontal scrolling sort buttons.
 * Desktop: Clickable column headers + drag handles.
 */
export function LedgerList({
    ledgers,
    type,
    onConfirmUpdate,
    onRevertUpdate,
    onReorder,
    isUpdating,
    isDragEnabled = false,
    userLatestConfirmationDate,
    updatingLedgerId,
}: LedgerListProps) {
    const [sortField, setSortField] = useState<SortField>('weight');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Drag is only allowed when explicitly enabled AND sorted by weight AND no mutation in progress
    const canDrag = isDragEnabled && sortField === 'weight' && !isUpdating;

    // dnd-kit sensors - only activate from drag handle, not from value displays
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require minimum drag distance before activating
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Assets show returns columns (MWRR, P&L, P&L%), debts do not
    const showReturnsColumns = type === 'asset';
    const typeLabel = type === 'asset' ? 'Asset' : 'Debt';
    const valueLabel = showReturnsColumns ? 'Value' : 'Balance';

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(field === 'label' ? 'asc' : 'desc'); // Default desc for numeric
        }
    };

    // Sort ledgers based on current sort field
    const sortedLedgers = useMemo(() => {
        return [...ledgers].sort((a, b) => {
            // When sorting by weight, always push archived items to the end
            if (sortField === 'weight') {
                const aArchived = a.archivedAt !== null && a.archivedAt > 0;
                const bArchived = b.archivedAt !== null && b.archivedAt > 0;
                if (aArchived && !bArchived) return 1;  // a is archived, push to end
                if (!aArchived && bArchived) return -1; // b is archived, push to end
            }

            let aValue: number | string | null;
            let bValue: number | string | null;

            switch (sortField) {
                case 'label':
                    aValue = a.label.toLowerCase();
                    bValue = b.label.toLowerCase();
                    break;
                case 'mwrr':
                    aValue = a.analytics?.mwrr ?? 0;
                    bValue = b.analytics?.mwrr ?? 0;
                    break;
                case 'profitLoss':
                    aValue = a.analytics?.profitLoss ?? 0;
                    bValue = b.analytics?.profitLoss ?? 0;
                    break;
                case 'gainPercentage':
                    aValue = a.analytics?.gainPercentage ?? null;
                    bValue = b.analytics?.gainPercentage ?? null;
                    // Push nulls to end
                    if (aValue === null && bValue === null) return 0;
                    if (aValue === null) return 1;
                    if (bValue === null) return -1;
                    break;
                case 'value':
                    aValue = a.latestValue?.convertedValue ?? 0;
                    bValue = b.latestValue?.convertedValue ?? 0;
                    break;
                case 'weight':
                    aValue = a.weight;
                    bValue = b.weight;
                    break;
                default:
                    return 0;
            }

            // Push zeros/nulls to the end for numeric fields (except weight)
            if (sortField !== 'label' && sortField !== 'weight') {
                if (aValue === 0 && bValue !== 0) return 1;
                if (bValue === 0 && aValue !== 0) return -1;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [ledgers, sortField, sortDirection]);

    // Local state for optimistic reordering - prevents flicker during API call
    const [localOrder, setLocalOrder] = useState<Ledger[]>(sortedLedgers);

    // Sync local order when sortedLedgers changes (new data from server or sort change)
    useEffect(() => {
        setLocalOrder(sortedLedgers);
    }, [sortedLedgers]);

    // Use local order for rendering to prevent snap-back
    const displayLedgers = canDrag ? localOrder : sortedLedgers;

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id || !onReorder) {
            return;
        }

        const oldIndex = localOrder.findIndex((l) => l.id === active.id);
        const newIndex = localOrder.findIndex((l) => l.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        // Immediately update local order for smooth visual feedback
        setLocalOrder(arrayMove(localOrder, oldIndex, newIndex));

        // Notify parent to persist the change
        onReorder(Number(active.id), newIndex);
    };

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortField !== field) {
            return (
                <span className="ml-1 inline-flex flex-col opacity-30">
                    <ChevronUpIcon className="h-3 w-3 -mb-1" />
                    <ChevronDownIcon className="h-3 w-3" />
                </span>
            );
        }
        return sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-4 w-4 ml-1" />
        ) : (
            <ChevronDownIcon className="h-4 w-4 ml-1" />
        );
    };

    // Mobile sort options
    const sortOptions: { field: SortField; label: string }[] = [
        { field: 'weight', label: 'Custom' },
        { field: 'label', label: 'Name' },
        { field: 'value', label: valueLabel },
        ...(showReturnsColumns ? [
            { field: 'mwrr' as SortField, label: 'MWRR' },
            { field: 'profitLoss' as SortField, label: 'P&L' },
            { field: 'gainPercentage' as SortField, label: 'P&L(%)' },
        ] : []),
    ];

    return (
        <div className="rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
            {/* Mobile Sort Controls */}
            <div className="lg:hidden p-3 bg-gray-50 dark:bg-dark border-b border-gray-300 dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Sort by:</span>
                    <HorizontalScroller label="Sort options" className="min-w-0 flex-1">
                        {sortOptions.map(({ field, label }) => (
                            <button
                                key={field}
                                onClick={() => handleSort(field)}
                                className={`px-3 py-1 text-xs rounded-full border shrink-0 flex items-center gap-1 ${
                                    sortField === field
                                        ? 'bg-accent text-white border-accent'
                                        : 'bg-white dark:bg-secondary border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white'
                                }`}
                            >
                                {label}
                                {sortField === field && (
                                    sortDirection === 'asc' ? (
                                        <ChevronUpIcon className="h-3 w-3" />
                                    ) : (
                                        <ChevronDownIcon className="h-3 w-3" />
                                    )
                                )}
                            </button>
                        ))}
                    </HorizontalScroller>
                </div>
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:flex text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold bg-gray-50 dark:bg-dark border-b border-gray-300 dark:border-gray-700">
                {/* Spacer for drag handle */}
                <div className="w-8" />

                <div
                    className="flex-1 px-4 py-3 cursor-pointer flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('label')}
                >
                    {typeLabel}
                    <SortIcon field="label" />
                </div>

                {showReturnsColumns && (
                    <>
                        <div
                            className="w-24 px-4 py-3 text-right cursor-pointer flex items-center justify-end gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            onClick={() => handleSort('mwrr')}
                        >
                            MWRR
                            <SortIcon field="mwrr" />
                        </div>
                        <div
                            className="w-28 px-4 py-3 text-right cursor-pointer flex items-center justify-end gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            onClick={() => handleSort('profitLoss')}
                        >
                            P&L
                            <SortIcon field="profitLoss" />
                        </div>
                        <div
                            className="w-28 px-4 py-3 text-right cursor-pointer flex items-center justify-end gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            onClick={() => handleSort('gainPercentage')}
                        >
                            P&L(%)
                            <SortIcon field="gainPercentage" />
                        </div>
                    </>
                )}

                <div
                    className="w-48 px-4 py-3 text-right cursor-pointer flex items-center justify-end gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort('value')}
                >
                    {valueLabel}
                    <SortIcon field="value" />
                </div>
            </div>

            {/* Ledger Rows with Drag-and-Drop */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={displayLedgers.map((l) => l.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {displayLedgers.map((ledger, index) => (
                        <SortableLedgerRow
                            key={ledger.id}
                            ledger={ledger}
                            type={type}
                            onConfirmUpdate={onConfirmUpdate}
                            onRevertUpdate={onRevertUpdate}
                            isUpdating={updatingLedgerId === ledger.id}
                            isDragEnabled={canDrag}
                            isLast={index === displayLedgers.length - 1}
                            userLatestConfirmationDate={userLatestConfirmationDate}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
}
